import { Button as ButtonPrimitive } from '@base-ui/react/button';
import { cn } from '../../common/functions/cn';
import useTestTag from '../../common/hooks/use-test-tag';
import OdinIcon, { type OdinIconName } from '../odin-icon/odin-icon.component';

/**
 * @deprecated Encodes six styles that collapse to three under the role-named tokens. Use
 * `ButtonV2Props['variant']` instead. See MYOSH-15204.
 */
export type ButtonType = 'default' | 'primary' | 'text';
/**
 * @deprecated `alternative` only shifted the resting shade one step darker, which the role-named
 * tokens flatten. Use `ButtonV2Props['variant']` instead. See MYOSH-15204.
 */
export type ButtonVariant = 'regular' | 'alternative';
export type ButtonHtmlType = 'submit' | 'reset' | 'button';

/**
 * @deprecated Use `ButtonV2Props` instead. See MYOSH-15204.
 */
export interface ButtonProps extends Omit<ButtonPrimitive.Props, 'type' | 'style'> {
  type?: ButtonType;
  variant?: ButtonVariant;
  classNames?: string;
  fullWidth?: boolean;
  hidden?: boolean;
  disabled?: boolean;
  htmlType?: ButtonHtmlType;
  style?: React.CSSProperties;
}

/**
 * @deprecated Use `ButtonV2Props` with an `icon-*` size instead. See MYOSH-15204.
 */
interface IconButtonProps extends Omit<ButtonProps, 'type' | 'children' | 'classNames'> {
  type?: ButtonHtmlType;
  icon: OdinIconName;
  iconClassName?: string;
  /** Optional tooltip text; applied to title & aria-label for accessibility */
  tooltip?: string;
}

const commonButtonStyles =
  'inline-flex sm:w-auto text-md leading-5 rounded-sm border outline-none justify-center transition-colors duration-200 min-w-[3rem] min-h-[2.25rem] py-2 px-5 font-medium focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50';

const buttonStylesMap: Record<ButtonType, Record<ButtonVariant, string>> = {
  primary: {
    regular: 'bg-primary hover:bg-primary/90 text-primary-foreground border-transparent',
    alternative: 'bg-primary hover:bg-primary/90 text-primary-foreground border-transparent',
  },
  default: {
    regular: 'bg-background hover:bg-accent text-primary border border-solid border-primary',
    alternative: 'hover:bg-accent text-primary border border-primary',
  },
  text: {
    regular: 'text-primary hover:text-primary/80 bg-transparent border-transparent',
    alternative: 'text-primary hover:bg-primary/40 hover:text-foreground bg-transparent border-transparent',
  },
};

const getButtonStyles = (type: ButtonType, variant: ButtonVariant) => {
  const styles = buttonStylesMap[type] || buttonStylesMap.default;
  return styles[variant];
};

/**
 * @deprecated Predates the design system and its props fight the layout. Use `ButtonV2` instead.
 *
 * `type="primary"` becomes `variant="default"`, `type="default"` (or no `type`) becomes
 * `variant="outline"`, and `type="text"` becomes `variant="ghost"`, or `variant="link"` inline in
 * prose. `variant` has no equivalent and collapses away.
 *
 * `htmlType` becomes the native `type`. This is the one silent failure in the migration: leaving
 * `htmlType="submit"` behind raises no type error, but `ButtonV2` defaults to `type="button"` and the
 * button quietly stops submitting its form.
 *
 * `fullWidth` and `classNames` are not carried over. Pass `className` instead, adding `w-full` only
 * where the layout actually wants it. `hidden` is no longer a styling prop, the native `hidden`
 * attribute does the same job. See MYOSH-15204.
 */
export default function Button({
  type = 'default',
  variant = 'regular',
  classNames,
  className,
  fullWidth = true,
  hidden = false,
  disabled = false,
  htmlType = 'button',
  form,
  ...props
}: ButtonProps) {
  const buttonStyles = cn(
    commonButtonStyles,
    { 'w-full': fullWidth },
    { 'opacity-50': disabled },
    getButtonStyles(type, variant),
    classNames,
    className,
    hidden && 'hidden'
  );

  return (
    <ButtonPrimitive
      data-slot="button"
      type={htmlType}
      className={buttonStyles}
      form={form}
      disabled={disabled}
      {...props}
    />
  );
}

/**
 * @deprecated Use `ButtonV2` with an `icon-*` size instead, passing the icon component as a child.
 *
 * The icon needs no size of its own: the button sizes it. Replace `tooltip` with a native
 * `aria-label`, plus `title` only where a visible hint is wanted, and give every icon-only button an
 * accessible name. A spinning icon takes `animate-spin` through `iconClassName`.
 *
 * The `data-testid="icon-button-<icon>"` stamp does not carry over. Specs select by role and name.
 * See MYOSH-15204.
 */
export function IconButton({
  onClick,
  className,
  type = 'button',
  form,
  disabled,
  icon,
  iconClassName,
  tooltip,
  ...rest
}: IconButtonProps) {
  const tagCreator = useTestTag('icon-button');
  const buttonStyles = cn(
    'focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2',
    { 'cursor-not-allowed opacity-50': disabled },
    className
  );

  return (
    <button
      type={type}
      onClick={onClick}
      className={buttonStyles}
      form={form}
      disabled={disabled}
      ref={tagCreator(icon.trim() ? icon.replace(/\s+/g, '-').toLowerCase() : '')}
      title={tooltip}
      aria-label={tooltip}
      {...rest}
    >
      <OdinIcon icon={icon} className={cn('size-5', iconClassName)} />
    </button>
  );
}

/**
 * @deprecated Use `ButtonV2` with `variant="link"` instead.
 *
 * The underline inverts: this renders underlined at rest and drops it on hover, whereas `ButtonV2`
 * underlines on hover. The resting colour differs too, `text-primary` here against `text-foreground`
 * there. See MYOSH-15204.
 */
export function Link({ classNames, className, type: _type, style, ...props }: ButtonProps) {
  const linkStyles = cn(
    'm-1 bg-transparent p-0 text-md text-primary leading-5 underline hover:no-underline focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-4',
    classNames,
    className
  );

  return <button className={linkStyles} type="button" style={style} {...props} />;
}
